import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { 
  Mountain, 
  Home,
  Users,
  Mic,
  Camera,
  SlidersHorizontal,
  Lightbulb,
  MessageCircle,
  ArrowRight,
  Check,
  Clock,
  Video,
  Music,
  Image
} from "lucide-react";

import JsonLd from "../components/JsonLd";
import { pic } from "../lib/pic";

const WHATSAPP_STUDIO = "https://wa.me/34632628051?text=Hi%20Steve!%20I'm%20interested%20in%20renting%20the%20studio.";

const StudioPage = () => {
  const heroImage = "https://customer-assets.emergentagent.com/job_b2d9ffd4-51f9-4581-8ff4-6d1d9943a74a/artifacts/wbol31ef_FB_IMG_1767282954137.jpg";

  const equipment = [
    { icon: Camera, title: "3x Sony ZV-E10", desc: "Multi-angle 4K video" },
    { icon: Mic, title: "Shure MV7 Mics", desc: "Broadcast-quality audio" },
    { icon: SlidersHorizontal, title: "Rodecaster Pro II", desc: "Professional mixing" },
    { icon: Lightbulb, title: "Studio Lighting", desc: "Professional & RGB" },
    { icon: Camera, title: "Insta360 GO 3S", desc: "4K vlogging (128GB)" },
    { icon: Mic, title: "RØDE Wireless Micro", desc: "Wireless mic system" },
    { icon: Camera, title: "DJI Mavic Mini", desc: "Aerial drone footage" }
  ];

  const studioSchemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Savi Coliving Studio Rental",
    "serviceType": "Recording Studio & Content Production Space",
    "description": "Professional recording studio in the mountains of Valencia, Spain. 4K multi-cam, broadcast audio, drone, natural light. Half-day from €150, full-day €250.",
    "image": heroImage,
    "provider": { "@type": "Organization", "name": "Savi Coliving", "url": "https://www.savicoliving.com" },
    "areaServed": { "@type": "Place", "name": "Valencia, Spain" },
    "offers": [
      { "@type": "Offer", "name": "Half-day studio rental", "price": "150", "priceCurrency": "EUR" },
      { "@type": "Offer", "name": "Full-day studio rental", "price": "250", "priceCurrency": "EUR" }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Studio Rental | Savi Coliving</title>
        <meta name="description" content="Rent a professional recording studio in the mountains of Valencia. 4K cameras, broadcast audio, natural light. Half-day from €150." />
        <meta property="og:title" content="Studio Rental at Savi Coliving" />
        <meta property="og:description" content="A professional recording space in the mountains of Valencia. 4K multi-cam, broadcast audio, stunning backdrop." />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.savicoliving.com/studio" />
      </Helmet>
      <JsonLd id="studio" data={studioSchemaData} />

      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="relative pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <img fetchpriority="high" decoding="async" {...pic("studio-hero")} alt="Recording studio at Savi Coliving" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-15% via-[#FBF7F1]/75 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">A studio that doesn't look like a studio.</h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base max-w-2xl">
                Professional equipment. Mountain views. Natural light. Half-day from €150.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* CTA Banner */}
      <div className="bg-[#FBF7F1] py-6 px-4 border-b border-stone-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/60 text-sm">
            <span className="font-medium text-[#2E2A26]">4K multi-cam · Broadcast audio · Drone B-roll</span> — Savi guests get 20% off
          </p>
          <a href={WHATSAPP_STUDIO} target="_blank" rel="noopener noreferrer" data-testid="hero-studio-cta">
            <Button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-8 py-5 text-sm font-medium tracking-wide">
              Check Availability <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>

      {/* Perfect For */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">Perfect for</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-8">Whatever you're creating</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Mic, label: "Podcasts", detail: "Multi-mic, multi-cam ready" },
              { icon: Video, label: "Video Content", detail: "3x 4K cameras included" },
              { icon: Music, label: "Music Sessions", detail: "Acoustic or recorded" },
              { icon: Image, label: "Photo Shoots", detail: "Natural light & backdrop" }
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center p-5 bg-[#F4ECE2] rounded-xl">
                <item.icon className="w-5 h-5 text-[#C85A38] mb-2.5" />
                <p className="text-[#2E2A26] text-sm font-medium mb-0.5">{item.label}</p>
                <p className="text-[#2E2A26]/40 text-xs">{item.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#2E2A26]/40 text-xs mt-6">
            Also used for: album covers, music videos, documentary content, interviews, creative retreats
          </p>
        </div>
      </section>

      {/* Images */}
      <section className="px-2 sm:px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 gap-2">
          <img loading="lazy" decoding="async" {...pic("studio-2", { sizes: "(min-width: 1024px) 512px, 50vw", fallback: 480 })} alt="Studio setup" className="w-full h-48 sm:h-64 object-cover rounded-lg" />
          <img loading="lazy" decoding="async" {...pic("studio-3", { sizes: "(min-width: 1024px) 512px, 50vw", fallback: 480 })} alt="Recording session" className="w-full h-48 sm:h-64 object-cover rounded-lg" />
        </div>
      </section>

      {/* What You Get */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">All included</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-8">Professional equipment. No extras to hire.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {equipment.map((item, i) => (
              <div key={i} className="bg-[#F4ECE2] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium">{item.title}</p>
                  <p className="text-[#2E2A26]/40 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 px-4 bg-[#2E2A26]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-3">Simple pricing</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-8">Everything included. No hidden fees.</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#C85A38]" />
                <p className="text-white/60 text-sm font-medium">Half Day</p>
              </div>
              <p className="text-[#C85A38] font-serif text-4xl mb-2">€150</p>
              <p className="text-white/30 text-xs">4 hours · All equipment included</p>
            </div>
            <div className="bg-white/5 border border-[#C85A38]/30 rounded-2xl p-6 relative">
              <span className="absolute -top-2.5 right-4 bg-[#C85A38] text-white text-[11px] px-3 py-1 rounded-full font-medium">Best value</span>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#C85A38]" />
                <p className="text-white/60 text-sm font-medium">Full Day</p>
              </div>
              <p className="text-[#C85A38] font-serif text-4xl mb-2">€250</p>
              <p className="text-white/30 text-xs">8 hours · All equipment included</p>
            </div>
          </div>
          <div className="space-y-2 mb-8">
            {[
              "All cameras, mics, lighting & mixing included",
              "Drone available for aerial B-roll",
              "Steve available as optional tech support",
              "Savi guests get 20% off"
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2">
                <Check className="w-3.5 h-3.5 text-[#C85A38]" />
                <p className="text-white/50 text-xs">{item}</p>
              </div>
            ))}
          </div>
          <a href={WHATSAPP_STUDIO} target="_blank" rel="noopener noreferrer" data-testid="pricing-studio-cta">
            <Button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-6 text-sm font-medium">
              Book Your Session <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </section>

      {/* The Space */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-3">The location</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">More than a studio</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed max-w-xl mx-auto mb-6">
            Set in a private villa in the Sierra Calderona mountains, 25 minutes from Valencia. Natural light, mountain views, pool, terrace — the entire property becomes your backdrop.
          </p>
          <p className="text-[#2E2A26]/40 text-xs">
            The property has been used for album covers, music videos, photo shoots, and documentary content.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">Ready to record?</h2>
          <p className="text-[#2E2A26]/50 text-sm mb-6">
            Message Steve on WhatsApp to check dates and discuss your project.
          </p>
          <a href={WHATSAPP_STUDIO} target="_blank" rel="noopener noreferrer" data-testid="final-studio-cta">
            <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-10 py-6 text-sm font-medium">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Steve
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default StudioPage;
